import type { Lesson } from "../types";
import { getSession } from "./auth";
import { apiFetch } from "./api";

/*
 * El progreso sigue a la PERSONA, no al equipo. localStorage queda como copia de
 * trabajo (responde al instante y aguanta un corte de red) y el servidor como la
 * de verdad: cada cambio se sube a los pocos segundos, y al entrar se baja lo
 * que la persona avanzó en otras computadoras. Ver backend/lambda/api/progreso.ts.
 */

const STORAGE_VERSION = 1;
const STORAGE_PREFIX = "ikids.learning-progress.v1";
const PROGRESS_EVENT = "ikids:learning-progress";
const MASTERY_THRESHOLD = 0.75;

export interface LessonProgress {
  lessonId: string;
  moduleSlug: string;
  grade: string;
  unitId?: string;
  lessonType?: Lesson["lessonType"];
  stepIndex: number;
  totalSteps: number;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
  bestScore?: number;
  maxScore?: number;
  attempts: number;
  standards: string[];
}

interface ProgressStore {
  version: typeof STORAGE_VERSION;
  lessons: Record<string, LessonProgress>;
}

export type StandardMasteryStatus = "mastered" | "developing" | "not_started";

export interface StandardMastery {
  code: string;
  status: StandardMasteryStatus;
  bestScorePercent?: number;
  completedLessons: number;
  alignedLessons: number;
}

export interface LessonProgressStatus {
  started: boolean;
  completed: boolean;
  percent: number;
  stepIndex: number;
  scorePercent?: number;
}

export interface CourseProgressSummary {
  completedLessons: number;
  totalLessons: number;
  progressPercent: number;
  averageScorePercent?: number;
  completedAssessments: number;
  totalAssessments: number;
  masteredStandards: number;
  developingStandards: number;
  totalStandards: number;
  standards: StandardMastery[];
  byLessonId: Record<string, LessonProgressStatus>;
  continueLessonId?: string;
}

function emptyStore(): ProgressStore {
  return { version: STORAGE_VERSION, lessons: {} };
}

// Oculta el nombre de usuario de la clave visible de localStorage. No pretende
// ser criptografía: solo mantiene los datos de cada cuenta separados sin dejar
// el identificador de Cognito expuesto en texto claro.
function userKey(): string {
  const username = getSession()?.user ?? "anonymous";
  let hash = 2166136261;
  for (let i = 0; i < username.length; i += 1) {
    hash ^= username.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${STORAGE_PREFIX}.${(hash >>> 0).toString(36)}`;
}

function progressKey(moduleSlug: string, lessonId: string): string {
  return `${moduleSlug}::${lessonId}`;
}

function standardCodes(lesson: Lesson): string[] {
  const codes = lesson.standards?.map((standard) => standard.code).filter(Boolean)
    ?? (lesson.standard ? [lesson.standard] : []);
  return [...new Set(codes)];
}

function readStore(): ProgressStore {
  if (typeof window === "undefined") return emptyStore();
  try {
    const raw = window.localStorage.getItem(userKey());
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<ProgressStore>;
    if (parsed.version !== STORAGE_VERSION || !parsed.lessons || typeof parsed.lessons !== "object") {
      return emptyStore();
    }
    return { version: STORAGE_VERSION, lessons: parsed.lessons };
  } catch {
    return emptyStore();
  }
}

function writeStore(store: ProgressStore, cambiadas: string[] = []): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(userKey(), JSON.stringify(store));
    if (cambiadas.length) marcarPendientes(cambiadas);
    window.dispatchEvent(new CustomEvent(PROGRESS_EVENT));
  } catch {
    // La lección sigue funcionando si el navegador bloquea o llena localStorage.
  }
}

/* ---------------- Sincronización con el servidor ---------------- */

const SUBIR_TRAS_MS = 2000;
let temporizador: number | undefined;

function esParticipante(): boolean {
  return getSession()?.cognitoRole === "participant";
}

// Claves cambiadas aquí y todavía no confirmadas por el servidor. En localStorage
// y no en memoria: si se cierra la pestaña antes de subir, se sube al volver.
function pendientesKey(): string {
  return `${userKey()}.pendientes`;
}
function leerPendientes(): string[] {
  try {
    return JSON.parse(window.localStorage.getItem(pendientesKey()) ?? "[]") as string[];
  } catch {
    return [];
  }
}
function guardarPendientes(claves: string[]): void {
  try {
    window.localStorage.setItem(pendientesKey(), JSON.stringify([...new Set(claves)]));
  } catch { /* cuota o modo privado: se reintenta en la próxima sincronización */ }
}
function marcarPendientes(claves: string[]): void {
  if (!esParticipante()) return;
  guardarPendientes([...leerPendientes(), ...claves]);
  window.clearTimeout(temporizador);
  temporizador = window.setTimeout(() => { void subirPendientes(); }, SUBIR_TRAS_MS);
}

const ratio = (p: LessonProgress) => (p.bestScore !== undefined && p.maxScore ? p.bestScore / p.maxScore : -1);
const menor = (a?: string, b?: string) => (!a ? b : !b ? a : a < b ? a : b);

/**
 * La misma lección avanzada en dos equipos: lo mejor de cada lado. MISMA regla
 * que `fusionar` en backend/lambda/api/progreso.ts — contrato duplicado a
 * propósito; si cambia allá, cambia aquí.
 */
export function fusionarProgreso(a: LessonProgress | undefined, b: LessonProgress): LessonProgress {
  if (!a) return b;
  const reciente = a.updatedAt >= b.updatedAt ? a : b;
  const mejor = ratio(a) >= ratio(b) ? a : b;
  return {
    ...reciente,
    startedAt: menor(a.startedAt, b.startedAt) ?? reciente.startedAt,
    updatedAt: reciente.updatedAt,
    completedAt: menor(a.completedAt, b.completedAt),
    bestScore: mejor.bestScore,
    maxScore: mejor.maxScore,
    attempts: Math.max(a.attempts ?? 0, b.attempts ?? 0),
  };
}

// Por campos y no con JSON.stringify: el servidor guarda JSONB, que reordena las
// claves, y dos objetos iguales se verían distintos en cada entrada.
function mismoAvance(a: LessonProgress, b: LessonProgress): boolean {
  return a.stepIndex === b.stepIndex && a.updatedAt === b.updatedAt && a.completedAt === b.completedAt
    && a.bestScore === b.bestScore && a.maxScore === b.maxScore && a.attempts === b.attempts;
}

async function subir(claves: string[]): Promise<boolean> {
  const store = readStore();
  const lessons = Object.fromEntries(claves.filter((k) => store.lessons[k]).map((k) => [k, store.lessons[k]]));
  if (Object.keys(lessons).length === 0) return true;
  try {
    const res = await apiFetch("/progreso", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ lessons }),
    });
    if (!res.ok) return false;
    // Lo que el servidor guardó ya viene fusionado: la copia local se queda igual.
    const { lessons: guardadas } = (await res.json()) as { lessons: Record<string, LessonProgress> };
    const actual = readStore();
    for (const [k, v] of Object.entries(guardadas ?? {})) actual.lessons[k] = fusionarProgreso(actual.lessons[k], v);
    writeStore(actual);
    return true;
  } catch {
    return false; // sin red: quedan pendientes
  }
}

async function subirPendientes(): Promise<void> {
  if (!esParticipante()) return;
  const claves = leerPendientes();
  if (claves.length === 0) return;
  if (await subir(claves)) {
    // Solo se quitan las que se mandaron: pudo haber cambios nuevos mientras tanto.
    guardarPendientes(leerPendientes().filter((k) => !claves.includes(k)));
  }
}

/**
 * Al entrar: baja lo que la persona avanzó en otros equipos, lo fusiona con lo
 * de aquí y sube lo que solo existe aquí (incluido el progreso de antes de que
 * hubiera servidor, que así no se pierde).
 */
export async function sincronizarProgreso(): Promise<void> {
  if (!esParticipante()) return;
  try {
    const res = await apiFetch("/progreso");
    if (!res.ok) return;
    const { lessons: remotas } = (await res.json()) as { lessons: Record<string, LessonProgress> };
    const store = readStore();
    const soloAqui: string[] = [];
    for (const [k, local] of Object.entries(store.lessons)) {
      const remota = remotas?.[k];
      if (!remota || !mismoAvance(fusionarProgreso(remota, local), remota)) soloAqui.push(k);
    }
    for (const [k, remota] of Object.entries(remotas ?? {})) store.lessons[k] = fusionarProgreso(store.lessons[k], remota);
    writeStore(store);
    guardarPendientes([...leerPendientes(), ...soloAqui]);
    await subirPendientes();
  } catch {
    // Sin red: se trabaja con la copia local y se sincroniza la próxima vez.
  }
}

function upsertLesson(
  moduleSlug: string,
  lesson: Lesson,
  totalSteps: number,
  update: (current: LessonProgress) => LessonProgress,
): LessonProgress {
  const store = readStore();
  const key = progressKey(moduleSlug, lesson.id);
  const now = new Date().toISOString();
  const current = store.lessons[key] ?? {
    lessonId: lesson.id,
    moduleSlug,
    grade: lesson.grade,
    unitId: lesson.unitId,
    lessonType: lesson.lessonType,
    stepIndex: 0,
    totalSteps: Math.max(1, totalSteps),
    startedAt: now,
    updatedAt: now,
    attempts: 0,
    standards: standardCodes(lesson),
  };
  const next = update({
    ...current,
    grade: lesson.grade,
    unitId: lesson.unitId,
    lessonType: lesson.lessonType,
    totalSteps: Math.max(1, totalSteps),
    standards: standardCodes(lesson),
    updatedAt: now,
  });
  store.lessons[key] = next;
  writeStore(store, [key]);
  return next;
}

export function getLessonProgress(moduleSlug: string, lessonId: string): LessonProgress | undefined {
  return readStore().lessons[progressKey(moduleSlug, lessonId)];
}

export function saveLessonStep(
  moduleSlug: string,
  lesson: Lesson,
  stepIndex: number,
  totalSteps: number,
): LessonProgress {
  return upsertLesson(moduleSlug, lesson, totalSteps, (current) => ({
    ...current,
    stepIndex: Math.max(0, Math.min(Math.max(0, totalSteps - 1), stepIndex)),
  }));
}

export function saveLessonScore(
  moduleSlug: string,
  lesson: Lesson,
  score: number,
  maxScore: number,
): LessonProgress {
  return upsertLesson(moduleSlug, lesson, lesson.steps.length, (current) => {
    const safeMax = Math.max(1, maxScore);
    const safeScore = Math.max(0, Math.min(safeMax, score));
    const previousRatio = current.bestScore !== undefined && current.maxScore
      ? current.bestScore / current.maxScore
      : -1;
    const nextRatio = safeScore / safeMax;
    return {
      ...current,
      attempts: current.attempts + 1,
      bestScore: nextRatio >= previousRatio ? safeScore : current.bestScore,
      maxScore: nextRatio >= previousRatio ? safeMax : current.maxScore,
    };
  });
}

export function completeLesson(moduleSlug: string, lesson: Lesson): LessonProgress {
  return upsertLesson(moduleSlug, lesson, lesson.steps.length, (current) => ({
    ...current,
    stepIndex: Math.max(0, lesson.steps.length - 1),
    completedAt: current.completedAt ?? new Date().toISOString(),
  }));
}

function scorePercent(progress?: LessonProgress): number | undefined {
  if (progress?.bestScore === undefined || !progress.maxScore) return undefined;
  return Math.round((progress.bestScore / progress.maxScore) * 100);
}

export function summarizeCourseProgress(
  moduleSlug: string,
  lessons: Lesson[],
): CourseProgressSummary {
  const store = readStore();
  const byLessonId: Record<string, LessonProgressStatus> = {};
  const progressRows = lessons.map((lesson) => ({
    lesson,
    progress: store.lessons[progressKey(moduleSlug, lesson.id)],
  }));

  let totalFraction = 0;
  for (const { lesson, progress } of progressRows) {
    const totalSteps = Math.max(1, progress?.totalSteps ?? lesson.steps.length);
    const fraction = progress?.completedAt
      ? 1
      : progress
        ? Math.min(0.99, (progress.stepIndex + 1) / totalSteps)
        : 0;
    totalFraction += fraction;
    byLessonId[lesson.id] = {
      started: Boolean(progress),
      completed: Boolean(progress?.completedAt),
      percent: Math.round(fraction * 100),
      stepIndex: progress?.stepIndex ?? 0,
      scorePercent: scorePercent(progress),
    };
  }

  const completedLessons = progressRows.filter(({ progress }) => Boolean(progress?.completedAt)).length;
  const assessments = progressRows.filter(({ lesson }) => lesson.lessonType === "assessment");
  const scoreRows = progressRows
    .map(({ progress }) => scorePercent(progress))
    .filter((value): value is number => value !== undefined);

  const allStandards = [...new Set(lessons.flatMap(standardCodes))].sort((a, b) => a.localeCompare(b, "es", { numeric: true }));
  const standards: StandardMastery[] = allStandards.map((code) => {
    const aligned = progressRows.filter(({ lesson }) => standardCodes(lesson).includes(code));
    const completedAligned = aligned.filter(({ progress }) => Boolean(progress?.completedAt));
    // Las lecciones conceptuales aportan desarrollo. El dominio exige evidencia
    // de una evaluación o proyecto completado y un resultado de 75% o más.
    const evidenceScores = aligned
      .filter(({ lesson, progress }) =>
        Boolean(progress?.completedAt)
        && (lesson.lessonType === "assessment" || lesson.lessonType === "project")
        && !lesson.id.toLowerCase().includes("diagnostic"))
      .map(({ progress }) => scorePercent(progress))
      .filter((value): value is number => value !== undefined);
    const bestScore = evidenceScores.length ? Math.max(...evidenceScores) : undefined;
    const status: StandardMasteryStatus = bestScore !== undefined && bestScore >= MASTERY_THRESHOLD * 100
      ? "mastered"
      : aligned.some(({ progress }) => Boolean(progress))
        ? "developing"
        : "not_started";
    return {
      code,
      status,
      bestScorePercent: bestScore,
      completedLessons: completedAligned.length,
      alignedLessons: aligned.length,
    };
  });

  const incompleteStarted = progressRows
    .filter(({ progress }) => progress && !progress.completedAt)
    .sort((a, b) => (b.progress?.updatedAt ?? "").localeCompare(a.progress?.updatedAt ?? ""));
  const firstIncomplete = progressRows.find(({ progress }) => !progress?.completedAt);
  const continueLessonId = incompleteStarted[0]?.lesson.id ?? firstIncomplete?.lesson.id;

  return {
    completedLessons,
    totalLessons: lessons.length,
    progressPercent: lessons.length ? Math.round((totalFraction / lessons.length) * 100) : 0,
    averageScorePercent: scoreRows.length
      ? Math.round(scoreRows.reduce((sum, value) => sum + value, 0) / scoreRows.length)
      : undefined,
    completedAssessments: assessments.filter(({ progress }) => Boolean(progress?.completedAt)).length,
    totalAssessments: assessments.length,
    masteredStandards: standards.filter((standard) => standard.status === "mastered").length,
    developingStandards: standards.filter((standard) => standard.status === "developing").length,
    totalStandards: standards.length,
    standards,
    byLessonId,
    continueLessonId,
  };
}

export function subscribeToLearningProgress(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(PROGRESS_EVENT, listener);
  return () => window.removeEventListener(PROGRESS_EVENT, listener);
}

export interface AllProgress {
  started: number;
  completed: number;
  /** Lecciones recientes (por updatedAt desc), hasta 20 crudas. La resolución a
   *  título/URL y el recorte a 5 —descartando módulos/lecciones obsoletos— lo hace
   *  la pantalla de perfil, porque necesita cargar el catálogo. */
  recent: { moduleSlug: string; lessonId: string; updatedAt: string; completed: boolean }[];
}

/**
 * Agrega el progreso de TODOS los módulos (no de uno): la copia local, que
 * `sincronizarProgreso` mantiene al día con lo que la persona hizo en otros equipos.
 */
export function getAllProgress(): AllProgress {
  const lessons = Object.values(readStore().lessons);
  const completed = lessons.filter((l) => l.completedAt).length;
  const recent = [...lessons]
    .sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""))
    .slice(0, 20)
    .map((l) => ({
      moduleSlug: l.moduleSlug,
      lessonId: l.lessonId,
      updatedAt: l.updatedAt,
      completed: Boolean(l.completedAt),
    }));
  return { started: lessons.length, completed, recent };
}
